import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { LoggerProvider } from '@shared';
import { IsAuthenticatedUseCase } from '@auth';
import { AppError } from '../models/app-error.model';
import { AppConfigService } from '../config/app-config.service';
import { SessionExpiredDialogService } from '../services/session-expired-dialog.service';
import { AccessDeniedDialogService } from '../services/access-denied-dialog.service';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const configService = inject(AppConfigService);
  const sessionExpiredDialog = inject(SessionExpiredDialogService);
  const accessDeniedDialog = inject(AccessDeniedDialogService);
  const isAuthenticated = inject(IsAuthenticatedUseCase);
  const logger = inject(LoggerProvider).createScoped('HttpErrorInterceptor');

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only handle auth errors for our own API requests, not external URLs
      const isOwnApiRequest = req.url.startsWith('/') || req.url.includes(configService.apiBaseUrl);

      // Handle 401 Unauthorized - show session expired dialog only when a session existed
      if (error.status === 401 && isOwnApiRequest) {
        logger.warn('401 Unauthorized - session expired');
        if (isAuthenticated.execute() || sessionExpiredDialog.wasShown()) {
          sessionExpiredDialog.show();
        }
      }

      // Handle 403 Forbidden - show access denied dialog
      if (error.status === 403 && isOwnApiRequest) {
        logger.warn('403 Forbidden - access denied');
        accessDeniedDialog.show();
      }

      // When responseType:'blob' is used (e.g. PDF export), Angular delivers error bodies as
      // Blob instead of parsed JSON. Parse it back to JSON so transformError can read code/message.
      if (error.error instanceof Blob && error.error.type === 'application/json') {
        return from(error.error.text()).pipe(
          switchMap((text) => {
            let parsed: unknown;
            try { parsed = JSON.parse(text); } catch { parsed = null; }
            const patched = new HttpErrorResponse({
              error: parsed,
              headers: error.headers,
              status: error.status,
              statusText: error.statusText,
              url: error.url ?? undefined,
            });
            return throwError(() => transformError(patched));
          }),
        );
      }

      return throwError(() => transformError(error));
    })
  );
};

/**
 * Transforms HttpErrorResponse to AppError
 */
function transformError(error: HttpErrorResponse): AppError {
  // If backend returns our standard format with code and message
  if (error.error?.code && error.error?.message) {
    return {
      code: error.error.code,
      message: error.error.message,
      statusCode: error.status,
      timestamp: new Date().toISOString(),
      field: error.error.field,
    };
  }

  // Network errors (no response from server)
  if (error.status === 0) {
    return {
      code: 'NETWORK_ERROR',
      message: 'Unable to connect to the server. Please check your internet connection.',
      statusCode: 0,
      timestamp: new Date().toISOString(),
    };
  }

  // Timeout errors
  if (error.status === 408 || error.statusText === 'timeout') {
    return {
      code: 'TIMEOUT_ERROR',
      message: 'The request timed out. Please try again.',
      statusCode: 408,
      timestamp: new Date().toISOString(),
    };
  }

  // Unauthorized
  if (error.status === 401) {
    return {
      code: 'UNAUTHORIZED',
      message: 'You are not authorized to perform this action.',
      statusCode: 401,
      timestamp: new Date().toISOString(),
    };
  }

  // Forbidden
  if (error.status === 403) {
    return {
      code: 'FORBIDDEN',
      message: 'You do not have permission to access this resource.',
      statusCode: 403,
      timestamp: new Date().toISOString(),
    };
  }

  // Not Found
  if (error.status === 404) {
    return {
      code: 'NOT_FOUND',
      message: 'The requested resource was not found.',
      statusCode: 404,
      timestamp: new Date().toISOString(),
    };
  }

  // Internal Server Error
  if (error.status === 500) {
    return {
      code: 'INTERNAL_SERVER_ERROR',
      message: 'An internal server error occurred. Please try again later.',
      statusCode: 500,
      timestamp: new Date().toISOString(),
    };
  }

  // Fallback for unexpected errors
  return {
    code: 'UNKNOWN_ERROR',
    message: error.message || 'An unexpected error occurred. Please try again.',
    statusCode: error.status || 0,
    timestamp: new Date().toISOString(),
  };
}
